/**
 * The save-version review moment (BLA-362/B1): before a commit is sent, the
 * provider sees exactly what changed (word-level diff, ADR-077) with an
 * editable AI-prefilled summary at the top, and must explicitly confirm.
 *
 * **Cancel never discards work.** This dialog only reads `content`/`diff`
 * from its parent and calls `onConfirm`/`onCancel` — it holds no copy of the
 * editor's content itself, so closing it (Cancel, Escape, or the backdrop)
 * leaves the parent's live editor state completely untouched. The only note
 * state this component owns is the "What changed?" text itself, which is
 * purely additive metadata on the commit, never the document body.
 *
 * **Once the commit is in flight the dialog locks.** Every cancel affordance —
 * the footer Cancel, the header Close, the backdrop and Escape — goes inert
 * while `committing`, because the commit POST is a governed clinical write
 * that cannot be un-sent: closing the dialog would not stop the version from
 * landing, so a control labelled "Cancel" must not claim otherwise.
 *
 * **No-change case is handled by the caller**, not here: a parent must not
 * open this dialog at all when the diff is empty (B1 — "say nothing changed,
 * never show an empty diff and ask to confirm"). This component always
 * assumes it is being shown because there IS something to review.
 *
 * **A failed preview fails loud.** Because the dialog is only ever opened when
 * there IS something to review, a `null` `diff` after `diffLoading` clears can
 * only mean the preview request failed. That renders an honest error and keeps
 * **Confirm** disabled — showing no review and still letting the save through
 * would silently defeat the one thing this dialog exists for.
 *
 * **Large diffs stay usable**: the dialog is a scrollable modal capped at a
 * comfortable viewport height (mirroring {@link CompareVersionsDialog}), so
 * a large change set scrolls instead of overflowing off-screen.
 */
import React, { useCallback, useEffect, useRef } from "react";

import DiffView from "./DiffView";
import type { VersionDiff } from "../../types";

/** Props for {@link SaveVersionReviewDialog}. */
export interface SaveVersionReviewDialogProps {
  /**
   * The diff of the pending commit against the current version. `null` once
   * `diffLoading` has cleared means the preview FAILED to load — the caller
   * only opens this dialog when there is something to review, so there is no
   * "no diff selected" state here.
   */
  diff: VersionDiff | null;
  /** True while the diff preview is still loading (shown before `diff` arrives). */
  diffLoading: boolean;
  /** `{ source id: title }` so inline citations resolve to human labels. */
  sourceTitles?: Record<string, string> | null;
  /** The "What changed?" note text (prefillable by an AI summary, editable, clearable). */
  note: string;
  /** Update the note text. */
  onNoteChange: (note: string) => void;
  /** True while the AI summary is still being generated (B3/B4 — non-blocking). */
  summaryLoading: boolean;
  /** Whether a schema-lock provider-approval checkbox must be shown/checked. */
  needsApproval: boolean;
  /** The current approval checkbox state. */
  approved: boolean;
  /** Update the approval checkbox state. */
  onApprovedChange: (approved: boolean) => void;
  /** Confirm the commit with the current note/approval. */
  onConfirm: () => void;
  /**
   * Cancel — returns to editing with all edits intact (never discards work).
   * Never invoked while `committing`: the write cannot be un-sent.
   */
  onCancel: () => void;
  /** True while the actual commit request is in flight. */
  committing: boolean;
}

/**
 * Render the save-version review + confirm dialog.
 *
 * @param props - The pending diff/summary state and the confirm/cancel handlers.
 * @returns The dialog element.
 */
export const SaveVersionReviewDialog: React.FC<
  SaveVersionReviewDialogProps
> = ({
  diff,
  diffLoading,
  sourceTitles,
  note,
  onNoteChange,
  summaryLoading,
  needsApproval,
  approved,
  onApprovedChange,
  onConfirm,
  onCancel,
  committing,
}) => {
  const cancelIfIdle = useCallback((): void => {
    if (committing) return;
    onCancel();
  }, [committing, onCancel]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") cancelIfIdle();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [cancelIfIdle]);

  const noteInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    noteInputRef.current?.focus();
  }, []);

  // The preview failed (`loadDiffPreview` resolves null on any API failure).
  // This is the review moment B1 exists to guarantee, so it fails LOUD: an
  // honest message replaces the diff and Confirm stays blocked rather than
  // letting a save through with nothing reviewed.
  const diffUnavailable = !diffLoading && diff === null;
  const confirmDisabled = committing || (needsApproval && !approved);
  // Confirm is available once the diff has loaded, even while the summary is
  // still populating (B4 — the summary must never block the dialog or the
  // confirm action).
  const canConfirm = !diffLoading && !diffUnavailable && !confirmDisabled;

  return (
    <div
      data-testid="save-version-review-dialog"
      role="dialog"
      aria-modal="true"
      aria-label="Review changes before saving"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-6"
      onClick={cancelIfIdle}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-3xl flex-col rounded-xl bg-white p-5 shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-800">
            Review changes before saving
          </h3>
          <button
            type="button"
            data-testid="save-version-review-cancel-x"
            onClick={cancelIfIdle}
            disabled={committing}
            aria-label="Cancel and keep editing"
            className="rounded px-2 py-1 text-sm font-medium text-slate-500 hover:bg-slate-100 disabled:opacity-40"
          >
            Close
          </button>
        </div>

        {/* Summary at top (B1/B3) — prefillable, always editable/clearable, never blocks Save. */}
        <div className="mb-3 space-y-1">
          <label
            htmlFor="save-version-review-note"
            className="text-xs font-medium text-slate-600"
          >
            What changed?{" "}
            {summaryLoading && (
              <span
                data-testid="summary-loading"
                className="font-normal text-slate-400"
              >
                Drafting a summary…
              </span>
            )}
          </label>
          <input
            id="save-version-review-note"
            ref={noteInputRef}
            type="text"
            value={note}
            onChange={(e) => onNoteChange(e.target.value)}
            placeholder="Optional — describe what changed"
            aria-label="What changed?"
            data-testid="save-version-review-note"
            className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
          />
        </div>

        {/* The diff itself — scrollable so a large change set stays usable. */}
        <div className="flex-1 overflow-y-auto rounded border border-slate-100 p-2">
          {diffLoading ? (
            <p
              data-testid="save-version-review-diff-loading"
              className="text-sm text-slate-400 italic"
            >
              Loading changes…
            </p>
          ) : diffUnavailable ? (
            <p
              data-testid="save-version-review-diff-error"
              className="text-sm text-rose-700"
            >
              Couldn&rsquo;t load the changes to review. Nothing has been saved
              — your edits are still here. Close this and try again.
            </p>
          ) : (
            <DiffView diff={diff} sourceTitles={sourceTitles} />
          )}
        </div>

        {needsApproval && (
          <label className="mt-3 flex items-center gap-2 text-xs text-amber-800">
            <input
              type="checkbox"
              checked={approved}
              onChange={(e) => onApprovedChange(e.target.checked)}
              aria-label="Approve section schema change"
            />
            I approve this change to the Blueprint section schema.
          </label>
        )}

        <div className="mt-4 flex items-center justify-end gap-3">
          <button
            type="button"
            data-testid="save-version-review-cancel"
            onClick={cancelIfIdle}
            disabled={committing}
            className="rounded px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="save-version-review-confirm"
            onClick={onConfirm}
            disabled={!canConfirm}
            className="rounded bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-40"
          >
            {committing ? "Saving…" : "Confirm and save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SaveVersionReviewDialog;
